import { cn } from '@creator/ui/utils/ui';
import useYahooAds from '@src/hooks/useYahooAds';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { memo, useEffect, useRef } from 'react';

declare global {
    interface Window {
        googletag: {
            cmd: Array<() => void>;
            defineSlot: (adUnitPath: string, sizes: number[] | number[][], divId: string) => any; // 'any' is googletag.Slot
            pubads: () => any; // 'any' is googletag.PubAdsService
            display: (divId: string) => void;
            destroySlots: (slots: any[]) => boolean; // 'any' is googletag.Slot
        };
    }
}

interface AdSlotProps {
    className?: string;
    adUnitPath: string;
    sizes: number[] | number[][];
    divId: string;
    targeting?: { key: string; value: string };
}

const GoogleAdSlot: React.FC<AdSlotProps> = props => {
    const { className, adUnitPath, sizes, divId, targeting } = props;
    const adRef = useRef<HTMLDivElement>(null);
    const { isLoaded } = useYahooAds();

    const slotRef = useRef<any>(null); // Stores the googletag.Slot object
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!isLoaded || !window.googletag || !window.googletag.cmd) {
            return;
        }

        const gpt = window.googletag;

        gpt.cmd.push(() => {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
            if (slotRef.current) {
                gpt.destroySlots([slotRef.current]);
                slotRef.current = null;
            }

            const slot = gpt.defineSlot(adUnitPath, sizes, divId);
            if (!slot) {
                console.warn(`[Ad Slot] Failed to define slot: ${divId} for path: ${adUnitPath}`);
                return;
            }

            slot.addService(gpt.pubads());
            slotRef.current = slot;

            slot.clearTargeting(); // Clear any previous targeting on this slot
            if (targeting?.key && targeting.value) {
                slot.setTargeting(targeting.key, targeting.value);
            }

            gpt.display(divId);
            gpt.pubads().refresh([slot]);

            intervalIdRef.current = setInterval(() => {
                if (slotRef.current && document.getElementById(divId)) {
                    console.log(`[Ad Refresh] Auto-refreshing slot: ${divId}`);
                    gpt.pubads().refresh([slotRef.current]);
                } else {
                    // Slot or element no longer exists, clear interval
                    if (intervalIdRef.current) clearInterval(intervalIdRef.current);
                }
            }, 30000);
        });

        return () => {
            if (gpt?.cmd) {
                gpt.cmd.push(() => {
                    if (intervalIdRef.current) {
                        clearInterval(intervalIdRef.current);
                        intervalIdRef.current = null;
                    }
                    if (slotRef.current) {
                        // Check if slot is still registered before destroying to avoid errors
                        const pubadsSlots = gpt.pubads().getSlots();
                        const currentSlotExists = pubadsSlots.some((s: any) => s === slotRef.current);
                        if (currentSlotExists) {
                            gpt.destroySlots([slotRef.current]);
                        }
                        slotRef.current = null;
                    }
                });
            }
        };
    }, [isLoaded, adUnitPath, JSON.stringify(sizes), divId, JSON.stringify(targeting)]); // Added more deps

    return <div className={cn('flex', className)} id={divId} ref={adRef} />;
};

export default memo(GoogleAdSlot, propsEqualityFn);
